import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useEngine } from '@/app/EngineContext';
import { usePaletteStore } from '@/store/paletteStore';
import { fuzzyRank, type FuzzyMatch } from '@/core/search/fuzzy';
import { buildPaletteCommands, type PaletteCommand } from '@/features/palette/paletteCommands';
import { activateResult, searchSpatial, type SearchResult } from '@/features/search/spatialSearch';
import { CommandDialog, CommandItem, EmptyRow, Kbd } from '@/components/ui';
import styles from './CommandPalette.module.css';

/**
 * Command palette (⇧⌘P) and spatial search (⌘F) share one surface. Commands are
 * data from `paletteCommands`; two-level commands push a child list; the "Search…"
 * command switches the query into spatial search. Keyboard only needs ↑ ↓ ↵ ⎋ ⌫.
 */
export const CommandPalette = () => {
  const open = usePaletteStore((s) => s.open);
  return open ? <PaletteDialog /> : null;
};

type Row =
  | {
      readonly kind: 'command';
      readonly command: PaletteCommand;
      readonly match: FuzzyMatch | null;
      readonly disabled: boolean;
    }
  | { readonly kind: 'result'; readonly result: SearchResult };

const PaletteDialog = () => {
  const { scene, applications, animator } = useEngine();
  const initialMode = usePaletteStore((s) => s.mode);
  const hide = () => {
    usePaletteStore.getState().hide();
  };
  const [mode, setMode] = useState(initialMode);
  const [query, setQuery] = useState('');
  const [stack, setStack] = useState<readonly PaletteCommand[]>([]);
  const [index, setIndex] = useState(0);

  const root = useMemo(() => buildPaletteCommands({ scene, applications, animator }), [scene, applications, animator]);
  const parent = stack[stack.length - 1];

  const rows: readonly Row[] = useMemo(() => {
    if (mode === 'search') return searchSpatial(query, applications).map((result) => ({ kind: 'result', result }));
    const source = parent?.children ? parent.children() : root;
    const ranked = query.trim()
      ? fuzzyRank(query, source, (c) => [c.title, c.group, ...(c.keywords ?? [])], 40)
      : source.map((item) => ({ item, match: null }));
    return ranked.map(({ item, match }) => ({
      kind: 'command',
      command: item,
      match,
      disabled: item.enabled ? !item.enabled() : false,
    }));
  }, [mode, query, parent, root, applications]);

  const clamped = Math.min(index, Math.max(0, rows.length - 1));

  useEffect(() => {
    document.getElementById(`palette-${clamped}`)?.scrollIntoView({ block: 'nearest' });
  }, [clamped, rows]);

  const activate = (row: Row | undefined) => {
    if (!row) return;
    if (row.kind === 'result') {
      hide();
      activateResult(row.result, scene, applications, animator);
      return;
    }
    const c = row.command;
    if (row.disabled) return;
    if (c.mode === 'search') {
      setMode('search');
      setQuery('');
      setIndex(0);
      return;
    }
    if (c.children) {
      setStack([...stack, c]);
      setQuery('');
      setIndex(0);
      return;
    }
    hide();
    c.run?.();
  };

  const back = () => {
    if (mode === 'search' && initialMode !== 'search') setMode('commands');
    else if (stack.length) setStack(stack.slice(0, -1));
    else return false;
    setQuery('');
    setIndex(0);
    return true;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    e.stopPropagation();
    const n = Math.max(1, rows.length);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setIndex((clamped + 1) % n);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setIndex((clamped - 1 + n) % n);
        break;
      case 'Enter':
        e.preventDefault();
        activate(rows[clamped]);
        break;
      case 'Escape':
        e.preventDefault();
        if (!back()) hide();
        break;
      case 'Backspace':
        if (query === '' && back()) e.preventDefault();
        break;
    }
  };

  const crumbs = [...(mode === 'search' ? ['Search'] : []), ...stack.map((c) => c.title.replace(/…$/, ''))];

  const activeId = rows[clamped] ? `palette-${clamped}` : undefined;
  const badgeColor: Record<SearchResult['kind'], string> = {
    object: 'var(--note-sand)',
    application: 'var(--note-sky)',
    workspace: 'var(--note-moss)',
  };

  return (
    <CommandDialog
      label={mode === 'search' ? 'Spatial search' : 'Command palette'}
      testId="palette"
      open
      onClose={hide}
      onKeyDown={onKeyDown}
      query={query}
      onQueryChange={(v) => {
        setQuery(v);
        setIndex(0);
      }}
      placeholder={mode === 'search' ? 'Search objects, applications, workspaces…' : 'Type a command…'}
      inputLabel={mode === 'search' ? 'Search' : 'Command'}
      inputTestId="palette-input"
      listId="palette-list"
      activeId={activeId}
      prefix={crumbs.map((c) => (
        <Chip
          key={c}
          label={c}
          color="primary"
          variant="outlined"
          sx={{ '& .MuiChip-label': { fontFamily: 'inherit', fontSize: 12 } }}
        />
      ))}
      footer="↑↓ navigate · ↵ run · ⌫ back · esc close"
      dataAttributes={{ 'data-mode': mode }}
    >
      {rows.length === 0 && <EmptyRow>Nothing matches “{query}”.</EmptyRow>}
      {rows.map((row, i) => (
        <CommandItem
          key={row.kind === 'command' ? row.command.id : `${row.result.kind}:${row.result.id}`}
          id={`palette-${i}`}
          role="option"
          aria-selected={i === clamped}
          aria-disabled={row.kind === 'command' && row.disabled ? true : undefined}
          selected={i === clamped}
          data-testid="palette-item"
          data-disabled={(row.kind === 'command' && row.disabled) || undefined}
          sx={{ gap: 2.5, opacity: row.kind === 'command' && row.disabled ? 0.45 : 1 }}
          onPointerEnter={() => {
            setIndex(i);
          }}
          onClick={() => {
            activate(row);
          }}
        >
          {row.kind === 'command' ? (
            <>
              <span className={styles.title}>
                <Highlighted text={row.command.title} match={row.match} />
              </span>
              <Typography variant="caption" color="text.disabled">
                {row.command.group}
              </Typography>
              {row.command.shortcut && <Kbd>{row.command.shortcut}</Kbd>}
            </>
          ) : (
            <>
              <Chip label={row.result.kind} variant="outlined" sx={{ bgcolor: badgeColor[row.result.kind] }} />
              <Box component="span" className={styles.title}>
                <Highlighted text={row.result.title} match={row.result.match} />
                <span className={styles.subtitle}>{row.result.subtitle}</span>
              </Box>
            </>
          )}
        </CommandItem>
      ))}
    </CommandDialog>
  );
};

const Highlighted = ({ text, match }: { text: string; match: FuzzyMatch | null }) => {
  if (!match || match.indices.length === 0) return <span>{text}</span>;
  // Match indices are code-unit positions (the matcher works on lowercased strings), so slice by index.
  const set = new Set(match.indices);
  const parts: { text: string; hit: boolean }[] = [];
  for (let i = 0; i < text.length; i++) {
    const hit = set.has(i);
    const last = parts[parts.length - 1];
    if (last?.hit === hit) last.text += text.charAt(i);
    else parts.push({ text: text.charAt(i), hit });
  }
  return <span>{parts.map((p, i) => (p.hit ? <mark key={i}>{p.text}</mark> : <span key={i}>{p.text}</span>))}</span>;
};
